// components/website/CheckoutPage.js - Fixed UPI for Capacitor App
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { dbGet, dbSet, cleanFirebaseData, sanitizeFirebasePath, dbPush } from '../../config/firebase';
import {
  X, Check, ChevronRight, ChevronLeft, CreditCard,
  Banknote, Mail, Loader, MapPin, User2, Phone,
  Home, Building, ArrowLeft, Truck, Clock, Package, AlertCircle,
  ShoppingBag, Heart, Shield, Star, Flower2, Sparkles
} from 'lucide-react';
import { toast } from 'react-toastify';

const RAZORPAY_KEY_ID = 'rzp_live_SzCL8zCUbCfIWs';

// Detect if running in Capacitor
const isCapacitor = () => {
  return typeof window !== 'undefined' && 
         (window.Capacitor !== undefined || 
          document.querySelector('meta[name="viewport"][content*="capacitor"]') !== null ||
          window.location.protocol === 'capacitor:');
};

const getSafeUserId = (user) => {
  if (!user) return 'anonymous_user';
  const userId = user.$id || user.uid || user.id;
  if (userId && typeof userId === 'string') return sanitizeFirebasePath(userId);
  if (user.email) return sanitizeFirebasePath(user.email);
  return 'anonymous_user';
};

// Load Razorpay script with retry mechanism
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      console.log('Razorpay already loaded');
      resolve(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="checkout.razorpay.com"]');
    if (existingScript) {
      existingScript.onload = () => {
        console.log('Razorpay script loaded from existing');
        resolve(true);
      };
      existingScript.onerror = () => resolve(false);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      console.log('Razorpay script loaded successfully');
      resolve(true);
    };
    script.onerror = (err) => {
      console.error('Failed to load Razorpay script:', err);
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

// Success Modal Component
const SuccessModal = ({ order, onClose, onViewOrders }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4000,
        backdropFilter: 'blur(4px)'
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: 'white', borderRadius: '20px', zIndex: 4001,
        width: '90%', maxWidth: '400px', padding: '32px', textAlign: 'center',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
      }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '32px', background: '#10b981',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
        }}>
          <Check size={32} color="white" />
        </div>
        
        <h2 style={{ fontSize: isMobile ? '1.2rem' : '1.3rem', fontWeight: 600, marginBottom: '8px', color: '#1e293b' }}>
          Order Placed!
        </h2>
        
        <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '8px' }}>
          Your order has been placed successfully.
        </p>
        
        <p style={{ 
          background: '#f8fafc', 
          padding: '8px 16px', 
          borderRadius: '8px', 
          display: 'inline-block',
          marginBottom: '20px'
        }}>
          Order ID: <strong style={{ color: '#dc2626' }}>{order?.id}</strong>
        </p>
        
        <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
          <button
            onClick={onViewOrders}
            style={{
              flex: 1,
              padding: '12px',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            View Orders
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              background: '#f1f5f9',
              color: '#64748b',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer'
            }}
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </>
  );
};

// UPI Icon Component
const UpiIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="6" width="16" height="12" rx="1" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 10H16" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 14H16" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M8 14H9" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const CheckoutPage = ({ onClose, onPlaceOrder, buyNowProduct: propBuyNowProduct }) => {
  const navigate = useNavigate();
  const { cart, totalPrice, clearCart } = useCart();
  const { user, updateProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState('upi');
  const [razorpayReady, setRazorpayReady] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successOrder, setSuccessOrder] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    permanentAddress: '',
    correspondenceAddress: '',
    landmark: '',
    city: 'Solapur',
    pinCode: '',
    village: ''
  });
  const [errors, setErrors] = useState({});
  const [profileLoaded, setProfileLoaded] = useState(false);
  const orderPlacedRef = useRef(false);

  // Get checkout items
  const getCheckoutItems = () => {
    if (propBuyNowProduct) {
      if (propBuyNowProduct.items) {
        return propBuyNowProduct.items;
      }
      return [propBuyNowProduct];
    }
    
    const stored = sessionStorage.getItem('buyNowProduct');
    if (stored) {
      const parsed = JSON.parse(stored);
      sessionStorage.removeItem('buyNowProduct');
      if (parsed.items) {
        return parsed.items;
      }
      return [parsed];
    }
    
    return cart.map(item => ({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      image: item.image,
      variantName: item.variantName,
      variantId: item.variantId
    }));
  };

  const [checkoutItems, setCheckoutItems] = useState(getCheckoutItems());
  const checkoutTotal = checkoutItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (onClose) onClose();
      else navigate(-1);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate, onClose]);

  const handleBack = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load Razorpay script
  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 3;

    const loadScript = async () => {
      try {
        const loaded = await loadRazorpayScript();
        if (mounted) {
          setRazorpayReady(loaded);
          if (!loaded && retryCount < maxRetries) {
            retryCount++;
            setTimeout(loadScript, 1000);
          } else if (!loaded) {
            toast.warning('Payment gateway not ready. Please use Cash on Delivery.');
          }
        }
      } catch (err) {
        console.error('Script load error:', err);
        if (mounted) setRazorpayReady(false);
      }
    };

    loadScript();
    return () => { mounted = false; };
  }, []);

  // Load user profile
  useEffect(() => {
    const loadProfileData = async () => {
      if (user && !profileLoaded) {
        try {
          const safeUserId = getSafeUserId(user);
          const userData = await dbGet(`users/${safeUserId}`);
          if (userData && Object.keys(userData).length > 0) {
            setFormData({
              fullName: userData.fullName || userData.name || user?.name || '',
              phone: userData.contactNumber || userData.phone || user?.phone || '',
              email: userData.email || user?.email || '',
              permanentAddress: userData.permanentAddress || '',
              correspondenceAddress: userData.correspondenceAddress || '',
              landmark: userData.landmark || '',
              city: userData.city || 'Solapur',
              pinCode: userData.pinCode || '',
              village: userData.village || ''
            });
          } else if (user) {
            setFormData({
              fullName: user?.name || user?.fullName || '',
              phone: user?.phone || user?.contactNumber || '',
              email: user?.email || '',
              permanentAddress: user?.permanentAddress || '',
              correspondenceAddress: user?.correspondenceAddress || '',
              landmark: user?.landmark || '',
              city: user?.city || 'Solapur',
              pinCode: user?.pinCode || '',
              village: user?.village || ''
            });
          }
          setProfileLoaded(true);
        } catch (error) {
          console.error('Error loading profile:', error);
        }
      }
    };
    loadProfileData();
  }, [user, profileLoaded]);

  const solapurPinCodes = ['413001', '413002', '413003', '413004', '413005', '413006', '413007', '413008', '413009', '413010', '413255', '413224', '413222'];

  const validateFullName = (name) => {
    if (!name.trim()) return 'Full name is required';
    if (name.trim().length < 3) return 'Name must be at least 3 characters';
    return '';
  };
  
  const validatePhone = (phone) => {
    if (!phone) return 'Phone number is required';
    if (!/^\d{10}$/.test(phone)) return 'Enter a valid 10-digit mobile number';
    return '';
  };
  
  const validateAddress = (address) => {
    if (!address.trim()) return 'Delivery address is required';
    if (address.trim().length < 10) return 'Enter a complete address';
    return '';
  };
  
  const validatePinCode = (pincode) => {
    if (!pincode) return 'Pin code is required';
    if (!/^\d{6}$/.test(pincode)) return 'Enter a valid 6-digit pin code';
    if (!solapurPinCodes.includes(pincode)) return 'Delivery only available in Solapur City.';
    return '';
  };

  const validateStep1 = () => {
    const newErrors = {};
    const nameError = validateFullName(formData.fullName);
    if (nameError) newErrors.fullName = nameError;
    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;
    const addressError = validateAddress(formData.permanentAddress);
    if (addressError) newErrors.permanentAddress = addressError;
    const pinCodeError = validatePinCode(formData.pinCode);
    if (pinCodeError) newErrors.pinCode = pinCodeError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleNextStep = () => {
    if (validateStep1()) setStep(2);
    else toast.error('Please fill all required fields correctly');
  };
  
  const handlePrevStep = () => setStep(1);

  const saveAddressToProfile = async () => {
    try {
      const safeUserId = getSafeUserId(user);
      const existingData = await dbGet(`users/${safeUserId}`) || {};
      
      const updatedProfile = cleanFirebaseData({
        ...existingData,
        fullName: formData.fullName,
        name: formData.fullName,
        contactNumber: formData.phone,
        phone: formData.phone,
        permanentAddress: formData.permanentAddress,
        correspondenceAddress: formData.correspondenceAddress,
        landmark: formData.landmark,
        pinCode: formData.pinCode,
        village: formData.village,
        city: formData.city,
        updatedAt: new Date().toISOString()
      });
      
      await dbSet(`users/${safeUserId}`, updatedProfile);
      if (updateProfile) await updateProfile(updatedProfile);
    } catch (error) {
      console.error('Error saving address to profile:', error);
    }
  };

  // Open Razorpay with UPI support for Capacitor
  const openRazorpay = () => {
    return new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        reject(new Error('Razorpay SDK not loaded. Please try again.'));
        return;
      }
      
      // Check if running in Capacitor
      const inCapacitor = isCapacitor();
      console.log('Running in Capacitor:', inCapacitor);
      
      // Base options
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: Math.round(checkoutTotal * 100),
        currency: 'INR',
        name: 'Sugandhi Flowers',
        description: `Order Payment - ${formData.fullName}`,
        image: 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=100&q=80',
        prefill: {
          name: formData.fullName,
          email: formData.email || 'customer@example.com',
          contact: formData.phone
        },
        notes: {
          address: formData.permanentAddress,
          order_type: 'flower_shop'
        },
        theme: {
          color: '#dc2626'
        },
        modal: {
          ondismiss: () => {
            reject(new Error('Payment cancelled by user'));
          },
          escape: false,
          confirm_close: true
        },
        handler: (response) => {
          console.log('Payment response:', response);
          resolve({ 
            success: true, 
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            signature: response.razorpay_signature
          });
        }
      };
      
      // For Capacitor, add specific configuration to force UPI methods
      if (inCapacitor) {
        // Override user agent detection by forcing mobile web view
        options.method = {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
          paylater: true,
          upi_qr: true,
          // Force all UPI methods
          upi_intent: true,
          upi_collect: true,
          upi_qr_code: true
        };
        
        // Add UPI specific configuration
        options.upi = {
          flow: 'collect', // Use collect flow for better compatibility
          intent: true,
          mode: 'both' // Allow both intent and QR
        };
        
        // Add configuration for better WebView compatibility
        options.config = {
          display: {
            language: 'en',
            theme: 'light'
          },
          checkout: {
            method: {
              upi: true,
              netbanking: true,
              card: true,
              wallet: true,
              paylater: true
            }
          }
        };
        
        // Add timeout for WebView
        options.timeout = 600; // 10 minutes timeout
      }
      
      try {
        const razorpay = new window.Razorpay(options);
        
        razorpay.on('payment.failed', (response) => {
          console.error('Payment failed:', response);
          reject(new Error(response.error?.description || 'Payment failed. Please try again.'));
        });
        
        // For Capacitor, add additional event handlers
        if (inCapacitor) {
          razorpay.on('modal.closed', () => {
            console.log('Modal closed');
          });
          
          razorpay.on('payment.success', (response) => {
            console.log('Payment success:', response);
          });
        }
        
        razorpay.open();
      } catch (err) {
        console.error('Error opening Razorpay:', err);
        reject(new Error('Unable to open payment gateway. Please try again.'));
      }
    });
  };

  const saveOrderToFirebase = async (orderData) => {
    const cleanOrder = cleanFirebaseData(orderData);
    const orderRef = await dbPush('orders', cleanOrder);
    return { ...orderData, firebaseKey: orderRef.key };
  };

  const handlePlaceOrder = async () => {
    if (isSubmitting || loading || orderPlacedRef.current) return;
    if (checkoutItems.length === 0) {
      toast.error('No items to checkout');
      return;
    }
    
    orderPlacedRef.current = true;
    setLoading(true);
    setIsSubmitting(true);
    
    try {
      await saveAddressToProfile();
      
      const orderId = 'ORD' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).substr(2, 4).toUpperCase();
      let transactionId = null, razorpayPaymentId = null, paymentStatus = 'pending';

      // Handle different payment methods
      if (selectedPayment !== 'cod') {
        // For all online payments (UPI, Card, NetBanking)
        if (!razorpayReady) {
          toast.info('Loading payment gateway...');
          const reloaded = await loadRazorpayScript();
          if (!reloaded) {
            toast.error('Payment gateway not available. Please use Cash on Delivery.');
            setLoading(false);
            setIsSubmitting(false);
            orderPlacedRef.current = false;
            return;
          }
          setRazorpayReady(true);
        }
        
        setPaymentProcessing(true);
        try {
          const paymentResult = await openRazorpay();
          if (paymentResult.success) {
            paymentStatus = 'completed';
            razorpayPaymentId = paymentResult.paymentId;
            transactionId = paymentResult.paymentId;
          } else {
            throw new Error('Payment failed');
          }
        } catch (paymentError) {
          console.error('Payment error:', paymentError);
          toast.error(paymentError.message || 'Payment failed. Please try again.');
          setPaymentProcessing(false);
          setLoading(false);
          setIsSubmitting(false);
          orderPlacedRef.current = false;
          return;
        }
        setPaymentProcessing(false);
      } else {
        transactionId = 'COD_' + Date.now().toString(36).toUpperCase();
      }

      // Get payment method display name
      let paymentMethodDisplay = '';
      if (selectedPayment === 'cod') {
        paymentMethodDisplay = 'Cash on Delivery';
      } else if (selectedPayment === 'upi') {
        paymentMethodDisplay = 'UPI Payment';
      } else {
        paymentMethodDisplay = 'Online Payment';
      }

      const cancellationOTP = selectedPayment === 'cod' ? Math.floor(100000 + Math.random() * 900000).toString() : null;

      const orderData = {
        id: orderId,
        transactionId,
        razorpayPaymentId,
        userId: getSafeUserId(user),
        customerName: formData.fullName,
        customerPhone: formData.phone,
        customerEmail: formData.email || '',
        items: checkoutItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity || 1,
          image: item.image,
          variant: item.variantName || item.variant || null,
          variantId: item.variantId || null
        })),
        totalAmount: checkoutTotal,
        paymentMethod: paymentMethodDisplay,
        selectedPaymentType: selectedPayment,
        paymentStatus: paymentStatus,
        orderStatus: 'pending',
        deliveryAddress: {
          line1: formData.permanentAddress,
          line2: formData.correspondenceAddress,
          landmark: formData.landmark,
          village: formData.village,
          city: formData.city,
          pincode: formData.pinCode
        },
        deliveryOTP: null,
        cancellationOTP: cancellationOTP,
        orderDate: new Date().toISOString(),
        statusHistory: [{
          status: 'pending',
          timestamp: new Date().toISOString(),
          message: selectedPayment === 'cod' 
            ? 'Order placed. Awaiting admin confirmation.'
            : 'Payment received. Order placed successfully.'
        }]
      };

      const savedOrder = await saveOrderToFirebase(orderData);
      
      // Clear cart only if this is a cart checkout
      if (!propBuyNowProduct && !sessionStorage.getItem('buyNowProduct')) {
        clearCart();
      }
      sessionStorage.removeItem('buyNowProduct');
      
      setSuccessOrder(savedOrder);
      setShowSuccessModal(true);
      setLoading(false);
      setIsSubmitting(false);
      
    } catch (error) {
      console.error('Order placement error:', error);
      toast.error(error.message || 'Failed to place order. Please try again.');
      setLoading(false);
      setIsSubmitting(false);
      orderPlacedRef.current = false;
      setPaymentProcessing(false);
    }
  };

  const handleViewActiveOrders = () => {
    setShowSuccessModal(false);
    if (onClose) onClose();
    navigate('/profile', { state: { defaultTab: 'active' } });
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    if (onClose) onClose();
  };

  if (paymentProcessing) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'white', borderRadius: '20px', padding: '40px', textAlign: 'center', maxWidth: '90%', width: '320px' }}>
          <Loader size={48} className="spinner" style={{ color: '#dc2626' }} />
          <h3 style={{ marginTop: '20px', fontSize: '1rem', color: '#1e293b' }}>Processing Payment...</h3>
          <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '8px' }}>Please don't close this window</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#f3f4f6', zIndex: 3000, overflow: 'auto' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '12px 16px' : '16px 24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={handleBack}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: '#dc2626',
                border: 'none',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '15px',
                color: '#fff',
                fontWeight: '900',
                borderRadius: '8px',
                padding: '8px 16px'
              }}
            >
              <ArrowLeft size={18} /> Back
            </button>
            
            <div>
              <h1 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 600, margin: 0, color: '#1f2937' }}>Complete Your Order</h1>
              <p style={{ fontSize: '10px', color: '#dc2626', marginTop: '2px' }}>Secure payment powered by Razorpay</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '16px' : '24px'
      }}>
        {/* Progress Steps */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: '20px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? '8px' : '16px' }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{
                width: isMobile ? '32px' : '36px',
                height: isMobile ? '32px' : '36px',
                borderRadius: '16px',
                background: step >= 1 ? '#dc2626' : '#e5e7eb',
                color: step >= 1 ? 'white' : '#9ca3af',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 6px',
                fontSize: '13px',
                fontWeight: 500
              }}>
                {step > 1 ? '✓' : '1'}
              </div>
              <span style={{ fontSize: isMobile ? '10px' : '11px', color: step >= 1 ? '#dc2626' : '#9ca3af' }}>Address</span>
            </div>
            <div style={{ flex: 0, color: '#d1d5db', fontSize: isMobile ? '14px' : '18px' }}>—</div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{
                width: isMobile ? '32px' : '36px',
                height: isMobile ? '32px' : '36px',
                borderRadius: '16px',
                background: step >= 2 ? '#dc2626' : '#e5e7eb',
                color: step >= 2 ? 'white' : '#9ca3af',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 6px',
                fontSize: '13px',
                fontWeight: 500
              }}>
                2
              </div>
              <span style={{ fontSize: isMobile ? '10px' : '11px', color: step >= 2 ? '#dc2626' : '#9ca3af' }}>Payment</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '24px' }}>
          {/* Form Section */}
          <div style={{ flex: 1.5 }}>
            {step === 1 ? (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: isMobile ? '20px' : '28px',
                border: '1px solid #e5e7eb'
              }}>
                <h2 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, marginBottom: '20px', color: '#1f2937' }}>Delivery Address</h2>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <label style={labelStyle}>Full Name <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      style={inputStyle}
                    />
                    {errors.fullName && <p style={errorStyle}>{errors.fullName}</p>}
                  </div>
                  
                  <div>
                    <label style={labelStyle}>Phone Number <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      maxLength={10}
                      placeholder="10-digit mobile number"
                      style={inputStyle}
                    />
                    {errors.phone && <p style={errorStyle}>{errors.phone}</p>}
                  </div>
                  
                  <div>
                    <label style={labelStyle}>Email (Optional)</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      style={inputStyle}
                    />
                  </div>
                  
                  <div>
                    <label style={labelStyle}>Delivery Address <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea
                      name="permanentAddress"
                      value={formData.permanentAddress}
                      onChange={handleChange}
                      rows={3}
                      placeholder="House/Flat No., Street, Area"
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                    {errors.permanentAddress && <p style={errorStyle}>{errors.permanentAddress}</p>}
                  </div>
                  
                  <div>
                    <label style={labelStyle}>Pin Code <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleChange}
                      maxLength={6}
                      placeholder="6-digit pin code"
                      style={inputStyle}
                    />
                    {errors.pinCode && <p style={{ ...errorStyle, color: '#f59e0b' }}>{errors.pinCode}</p>}
                  </div>
                  
                  <button
                    onClick={handleNextStep}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: 600,
                      fontSize: '15px',
                      cursor: 'pointer',
                      marginTop: '8px'
                    }}
                  >
                    Continue to Payment <ChevronRight size={16} style={{ display: 'inline', marginLeft: '6px' }} />
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: isMobile ? '20px' : '28px',
                border: '1px solid #e5e7eb'
              }}>
                <h2 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, marginBottom: '20px', color: '#1f2937' }}>Payment Method</h2>
                
                {/* Payment Options */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                
                  
                  {/* UPI Payment */}
                  <div
                    onClick={() => setSelectedPayment('upi')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '16px',
                      border: selectedPayment === 'upi' ? '2px solid #dc2626' : '1px solid #e5e7eb',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      background: selectedPayment === 'upi' ? '#fef2f2' : 'white'
                    }}
                  >
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: selectedPayment === 'upi' ? '#dc2626' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <UpiIcon color={selectedPayment === 'upi' ? 'white' : '#6b7280'} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: '#1f2937' }}>UPI Payment</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Google Pay, PhonePe, Paytm, BHIM UPI</div>
                    </div>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '10px',
                      border: selectedPayment === 'upi' ? '5px solid #dc2626' : '1px solid #d1d5db',
                      background: 'white'
                    }} />
                  </div>
                  
                  {/* Card / NetBanking */}
                  <div
                    onClick={() => setSelectedPayment('card')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '16px',
                      border: selectedPayment === 'card' ? '2px solid #dc2626' : '1px solid #e5e7eb',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      background: selectedPayment === 'card' ? '#fef2f2' : 'white'
                    }}
                  >
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: selectedPayment === 'card' ? '#dc2626' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CreditCard size={22} color={selectedPayment === 'card' ? 'white' : '#6b7280'} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: '#1f2937' }}>Card / NetBanking</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Credit/Debit Cards & Internet Banking</div>
                    </div>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '10px',
                      border: selectedPayment === 'card' ? '5px solid #dc2626' : '1px solid #d1d5db',
                      background: 'white'
                    }} />
                  </div>
                </div>

                  {/* Cash on Delivery */}
                  <div
                    onClick={() => setSelectedPayment('cod')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '16px',
                      border: selectedPayment === 'cod' ? '2px solid #dc2626' : '1px solid #e5e7eb',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      background: selectedPayment === 'cod' ? '#fef2f2' : 'white'
                    }}
                  >
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: selectedPayment === 'cod' ? '#dc2626' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Banknote size={22} color={selectedPayment === 'cod' ? 'white' : '#6b7280'} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: '#1f2937' }}>Cash on Delivery</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Pay when you receive the order</div>
                    </div>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '10px',
                      border: selectedPayment === 'cod' ? '5px solid #dc2626' : '1px solid #d1d5db',
                      background: 'white'
                    }} />
                  </div>

                {/* Delivery Address Summary */}
                <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '14px', marginBottom: '20px' }}>
                  <p style={{ fontSize: '13px', color: '#1f2937', marginBottom: '8px', fontWeight: 500 }}>Deliver to:</p>
                  <p style={{ fontSize: '12px', color: '#6b7280' }}>{formData.fullName} | {formData.phone}</p>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{formData.permanentAddress}, {formData.city} - {formData.pinCode}</p>
                  <button onClick={handlePrevStep} style={{ marginTop: '10px', padding: '4px 8px', background: 'none', border: 'none', color: '#dc2626', fontSize: '12px', cursor: 'pointer' }}>Edit Address</button>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handlePrevStep}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: '#f3f4f6',
                      color: '#4b5563',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    <ChevronLeft size={16} style={{ display: 'inline', marginRight: '6px' }} /> Back
                  </button>
                  <button
                    onClick={handlePlaceOrder}
                    disabled={loading}
                    style={{
                      flex: 2,
                      padding: '14px',
                      background: loading ? '#9ca3af' : '#fb641b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? <Loader size={18} className="spinner" style={{ display: 'inline', marginRight: '6px' }} /> : null}
                    {selectedPayment === 'cod' ? `Place Order (₹${checkoutTotal.toLocaleString()})` : `Pay ₹${checkoutTotal.toLocaleString()}`}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary - Right Sidebar */}
          <div style={{ flex: 1 }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              position: 'sticky',
              top: isMobile ? 'auto' : '90px'
            }}>
              <h3 style={{
                fontSize: '15px',
                fontWeight: 600,
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid #e5e7eb',
                color: '#1f2937'
              }}>
                Order Summary ({checkoutItems.length} items)
              </h3>
              
              <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
                {checkoutItems.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '16px', paddingBottom: '12px', borderBottom: i !== checkoutItems.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <img src={item.image} alt={item.name} style={{ width: '55px', height: '55px', objectFit: 'cover', borderRadius: '10px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: '#1f2937' }}>{item.name}</div>
                      {item.variantName && item.variantName !== 'Standard' && <div style={{ fontSize: '11px', color: '#6b7280' }}>{item.variantName}</div>}
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Qty: {item.quantity || 1}</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#dc2626', marginTop: '4px' }}>₹{(item.price * (item.quantity || 1)).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                  <span style={{ color: '#6b7280' }}>Subtotal</span>
                  <span style={{ color: '#1f2937' }}>₹{checkoutTotal.toLocaleString()}</span>
                </div>
               
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '12px', borderTop: '1px dashed #e5e7eb', fontWeight: 600, fontSize: '16px' }}>
                  <span style={{ color: '#1f2937' }}>Total</span>
                  <span style={{ color: '#dc2626' }}>₹{checkoutTotal.toLocaleString()}</span>
                </div>
              </div>
              
              <div style={{ marginTop: '16px', padding: '12px', background: '#f9fafb', borderRadius: '10px', textAlign: 'center' }}>
                <Truck size={16} color="#6b7280" style={{ marginBottom: '6px' }} />
                <div style={{ fontSize: '11px', color: '#6b7280' }}>Free delivery • Secure payments • Fresh flowers</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .spinner { animation: spin 0.6s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        input:focus, textarea:focus {
          outline: none;
          border-color: #dc2626 !important;
          box-shadow: 0 0 0 2px rgba(220,38,38,0.1) !important;
        }
      `}</style>

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal order={successOrder} onClose={closeSuccessModal} onViewOrders={handleViewActiveOrders} />
      )}
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#4b5563',
  marginBottom: '6px'
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  fontSize: '14px',
  outline: 'none',
  transition: 'all 0.2s',
  fontFamily: 'inherit',
  background: 'white'
};

const errorStyle = {
  color: '#dc2626',
  fontSize: '12px',
  marginTop: '4px'
};

export default CheckoutPage;